import { AccountService } from "@3gaam/api/services";
import { onAxiosRequest, onAxiosResponse } from "@3gaam/capp/src/axiosConfig";
import { BackgroundHooks } from "@3gaam/capp/src/hooks/backgroundHooks";
import mainRoutes from "@3gaam/capp/src/routes/main";
import {
  setIsLoggedInAction,
  setIsTokenValidAction,
} from "@3gaam/capp/src/store/auth";
import { setShowModalAction } from "@3gaam/capp/src/store/general";
import { useAppDispatch, useAppSelector } from "@3gaam/capp/src/store/hooks";
import VersionManagerModal from "@3gaam/capp/src/versionManagerModal";
import "@3gaam/shared/assets/scss/style.scss";
import { ReactNotifications } from "@3gaam/shared/components/general/notification";
import PageLoader from "@3gaam/shared/components/general/pageLoader";
import { Redirect } from "@3gaam/shared/components/general/redirect";
import {
  localStorageGet,
  localStorageSet,
} from "@3gaam/shared/utils/localStorage";
import { Suspense, useCallback, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { install } from "resize-observer";
import { ROUTES } from "./constants";
import { store } from "./store";
import { GetGoftinoUser } from"@3gaam/capp/src/utils/goftino";
import { useGoftino } from "@3gaam/shared/hooks/useGoftino";


function validateCurrentToken() {
  // note: having token in localStorage doesn't mean that is valid
  const authData = localStorageGet("auth");
  if (authData?.AccessToken) {
    store.dispatch(setIsTokenValidAction(true));
    return;
  }

  AccountService.identity({ Role: "Consultant" })
    .then((res) => {
      const data = res.data;
      const { Identified, Token } = data;

      if (!Identified) {
        localStorageSet("auth", Token);
      }

      store.dispatch(setIsTokenValidAction(true));
    })
    .catch((error) => {
      if (error?.status === 401 || error?.status === 403) {
        localStorage.removeItem("auth");
        store.dispatch(setIsLoggedInAction(false));
        store.dispatch(setIsTokenValidAction(false));
      }
    });
}

const App = () => {
  install();
  let dispatch = useAppDispatch();
  let showUpdateModal = useCallback(
    ({ ignorable, message, latestVersion }) => {
      dispatch(
        setShowModalAction("updateModal", true, {
          ignorable,
          message,
          latestVersion,
        }),
      );
    },
    [dispatch],
  );

  const goftinoUser = GetGoftinoUser();
  useGoftino({ goftinoUser: goftinoUser, initialMarginBottom: null });
  
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  const isCurrentTokenValid = useAppSelector(
    (state) => state.auth.isTokenValid,
  );

  onAxiosRequest();
  onAxiosResponse(showUpdateModal);

  useEffect(() => {
    validateCurrentToken();
  }, []);

  if (
    (isLoggedIn && !isCurrentTokenValid) ||
    typeof isCurrentTokenValid === "undefined"
  ) {
    return <PageLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <ReactNotifications />
      <BackgroundHooks />
      <Router>
        <Routes>
          {mainRoutes.map((route, index) => {
            const Layout =
              route.layout ||
              (({ children }) => {
                return children;
              });
            if (route.redirect)
              return (
                <Route
                  path={route.path}
                  element={<Redirect route={route} />}
                  key={route.path + index}
                />
              );
            else if (route.loginRequired && !isLoggedIn) {
              return (
                <Route
                  path={route.path}
                  element={
                    <Redirect route={{ ...route, to: ROUTES.LOGIN.PATH }} />
                  }
                  key={route.path + index}
                />
              );
            }
            return (
              <Route
                path={route.path}
                element={
                  <Layout title={route.title}>
                    <route.component subRoutes={route?.subRoutes} />
                  </Layout>
                }
                key={route.path + index}
              >
                {route.subRoutes
                  ? route.subRoutes.map((subRoute, index) => {
                      return (
                        <Route
                          path={subRoute.path}
                          element={
                            <>
                              <subRoute.component {...subRoute} />
                            </>
                          }
                          key={subRoute.title + index}
                        />
                      );
                    })
                  : null}
              </Route>
            );
          })}
        </Routes>
      </Router>
      <VersionManagerModal />
    </Suspense>
  );
};

export default App;
